import type { GradingProvider } from '../../lib/types'
import { EvoLogoFull, EvoLogoFullGradientSvg, ProviderLogo, ProviderLogoSvg } from '../icons'
import styles from './slab.module.css'

interface SlabHeaderProps {
  cardName: string
  cardNumber: string
  officialCount: string
  setName: string
  language: string
  grade: string
  provider: GradingProvider
  confidencePercent: number | null
  useInlineLogos?: boolean
}

/* HERO_TEXT_GRADIENT — white top → grey bottom, masked onto every glyph/logo */
const TEXT_GRADIENT = 'linear-gradient(180deg, #FFFFFF, #999999)'

/**
 * Exact port of the app's 2D SlabHeader (owen/grade-page): evo wordmark
 * (64×19), card name + number, set • language on the left; 64px grade number,
 * provider logo (h16) + confidence % on the right. Every text element is
 * filled with the white→grey HERO_TEXT_GRADIENT; the confidence % keeps its
 * semantic color (green ≥70%, orange below).
 */
export function SlabHeader({
  cardName,
  cardNumber,
  officialCount,
  setName,
  language,
  grade,
  provider,
  confidencePercent,
  useInlineLogos = false,
}: SlabHeaderProps) {
  const confidenceColor =
    confidencePercent === null
      ? '#9ba1a6'
      : confidencePercent < 70
        ? '#f39600'
        : '#56e000'

  return (
    <header className={styles.headerRow}>
      <div className={styles.headerLeft}>
        <span className={styles.headerBrand}>
          {useInlineLogos ? (
            <EvoLogoFullGradientSvg height={23} />
          ) : (
            <EvoLogoFull height={23} fill={TEXT_GRADIENT} />
          )}
        </span>
        <span className={styles.headerTitleRow}>
          <span className={`${styles.gradientText} ${styles.headerName}`}>
            {cardName}
          </span>
          {cardNumber ? (
            <span className={`${styles.gradientText} ${styles.headerNumber}`}>
              {cardNumber}/{officialCount}
            </span>
          ) : null}
        </span>
        <span className={styles.headerSubtitleRow}>
          {setName ? (
            <span className={`${styles.gradientText} ${styles.headerSubtitle}`}>
              {setName}
            </span>
          ) : null}
          {setName && language ? (
            <span className={`${styles.gradientText} ${styles.headerSubtitle}`}>
              •
            </span>
          ) : null}
          {language ? (
            <span className={`${styles.gradientText} ${styles.headerSubtitle}`}>
              {language}
            </span>
          ) : null}
        </span>
      </div>
      <div className={styles.headerGradeColumn}>
        <span className={styles.gradeNumberRow}>
          <span className={`${styles.gradientText} ${styles.grade}`}>{grade}</span>
        </span>
        <span className={styles.headerConditionRow}>
          {useInlineLogos ? (
            <ProviderLogoSvg provider={provider} height={16} gradient />
          ) : (
            <ProviderLogo
              provider={provider}
              height={16}
              fill={TEXT_GRADIENT}
            />
          )}
          {confidencePercent !== null ? (
            <span className={styles.headerConfidence} style={{ color: confidenceColor }}>
              {confidencePercent}%
            </span>
          ) : null}
        </span>
      </div>
    </header>
  )
}
